import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Form, Table } from "react-bootstrap";
import { getPerformance } from "../../api/tooling";
import { useAuth } from "../../context/AuthProvider";
import { ErrorNotice, MakerBadge } from "./ToolingShared";

const indiaDate = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
export default function PerformancePanel() {
  const { user } = useAuth();
  const [start, setStart] = useState(() => `${indiaDate().slice(0, 7)}-01`);
  const [end, setEnd] = useState(indiaDate);
  const valid = !!start && !!end && start <= end;
  const report = useQuery({
    queryKey: ["tooling", "performance", start, end],
    queryFn: () => getPerformance(start, end),
    enabled: user?.role === "superadmin" && valid,
  });
  if (user?.role !== "superadmin") return null;
  const max = Math.max(1, ...(report.data?.makers.map((m) => m.points) || []));
  return (
    <section
      className="border rounded p-3 my-3"
      aria-label="Die maker performance"
    >
      <h4>Die maker performance</h4>
      <div className="tooling-toolbar">
        <Form.Group controlId="performance-start">
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </Form.Group>
        <Form.Group controlId="performance-end">
          <Form.Label>Through</Form.Label>
          <Form.Control
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </Form.Group>
      </div>
      {!valid && <p role="alert">Choose a valid date range.</p>}
      <ErrorNotice error={report.error} retry={() => report.refetch()} />
      {valid && report.isLoading && <p>Loading performance…</p>}
      {valid && report.data && (
        <>
          <p>
            {report.data.allocation} Dates use India time. Archived completed
            cards are included.
          </p>
          <p className="fw-bold">
            {report.data.total_points.toLocaleString()} points ·{" "}
            {report.data.completed_cards} completed cards
          </p>
          <div
            role="img"
            aria-label="Points by die maker; exact values in the table below"
          >
            {report.data.makers.map((m) => (
              <div className="tooling-chart-row" key={m.id}>
                <MakerBadge maker={m} />
                <div className="tooling-chart-track">
                  <div
                    className="tooling-chart-bar"
                    style={{
                      width: `${(m.points / max) * 100}%`,
                      background: m.color,
                    }}
                  />
                </div>
                <span>{m.points.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <Table responsive striped>
            <thead>
              <tr>
                <th>Die maker</th>
                <th>Completed cards contributed to</th>
                <th>Allocated points</th>
              </tr>
            </thead>
            <tbody>
              {report.data.makers.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>{m.completed_cards}</td>
                  <td>{m.points.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {!report.data.completed_cards && (
            <p>No completed work in this date range.</p>
          )}
          {!!report.data.months.length && (
            <>
              <h5>Monthly output</h5>
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Total points</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.months.map((m) => (
                    <tr key={m.month}>
                      <td>{m.month}</td>
                      <td>{m.points.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </>
      )}
    </section>
  );
}
